/**
 * SmartImprove — Public Workshop Intake (Feb 2026 redesign).
 *
 * Four-step workshop-driven assessment with draft + resume so consultants
 * can capture findings across hours or multiple workshop days. NO results,
 * insights, ROI, or recommendations are exposed to the public submitter
 * — generated outputs land only on the consultant dashboard after triage.
 *
 *   Step 1 · Client Details                  → creates a draft + token
 *   Step 2 · Business & Warehouse Scope     → engagement context + sites
 *   Step 3 · Operational Assessment         → grouped findings + KPIs
 *   Step 4 · Assessment Submitted           → thank-you confirmation
 *
 * Backend contract: all writes go to `/api/smartimprove/intake/*` (public,
 * token-gated). The consultant CRUD API remains untouched.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { INDUSTRIES as SHARED_INDUSTRIES } from "../data/industries";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Warehouse,
  Briefcase,
  Target,
  Factory,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  startIntakeDraft,
  resumeIntakeDraft,
  patchEngagementDraft,
  addSiteDraft,
  updateSiteDraft,
  deleteSiteDraft,
  addFindingDraft,
  updateFindingDraft,
  deleteFindingDraft,
  submitIntakeDraft,
  listDraftRefs,
  saveDraftRef,
  clearDraftRef,
  getIntakeTaxonomy,
  getIssueTemplateSuggestions,
  getKpiTemplateSuggestions,
} from "../lib/smartimprovePublicIntakeApi";
import SmartImproveLoadDemoButton from "../components/SmartImproveLoadDemoButton";
import { applySmartImproveDemoFromPayload } from "../lib/api";

// ---------------------------------------------------------------------------
// Static taxonomies (mirrors backend `OPERATIONAL_AREAS` + admin defaults).
// Sub-process suggestions are intentionally inline so the public page has
// zero auth-gated dependencies.
// ---------------------------------------------------------------------------
const STEPS = [
  { id: 1, label: "Client & Engagement Details", short: "Client Details" },
  { id: 2, label: "Business & Warehouse Scope", short: "Business & Warehouse" },
  { id: 3, label: "Operational Findings Assessment", short: "Operational Findings" },
];
const TOTAL_STEPS = STEPS.length;
const STEP_TITLE = Object.fromEntries(STEPS.map((s) => [s.id, s.label]));

const BUSINESS_PROCESSES = [
  { key: "inbound", label: "Inbound Processes", sub: ["Receiving", "Unloading", "Dock Scheduling", "Putaway", "Cross-Docking"] },
  { key: "outbound", label: "Outbound Processes", sub: ["Wave Release", "Picking", "Packing", "Loading", "Dispatch"] },
  { key: "internal_warehouse", label: "Internal Warehouse Operations", sub: ["Replenishment", "Slotting", "Cycle Counting", "Returns"] },
  { key: "automation", label: "Automation & Warehouse Technology", sub: ["RF / Mobility", "Conveyors", "AS/RS", "AMR / AGV", "Voice / Vision"] },
  { key: "integration_interfaces", label: "Integration & Interfaces", sub: ["ERP↔WMS", "Carrier APIs", "Customer EDI", "Master Data"] },
  { key: "reporting", label: "Reporting, KPIs & Visibility", sub: ["Operational KPIs", "Labor KPIs", "Inventory KPIs", "Executive Reporting"] },
  { key: "yard_transport", label: "Yard & Transportation", sub: ["Yard Management", "Carrier Coordination", "Appointment Mgmt"] },
  { key: "labor_workforce", label: "Labor & Workforce Operations", sub: ["Scheduling", "Productivity", "Training", "Engagement"] },
  { key: "quality_compliance", label: "Quality & Compliance", sub: ["Audit Trails", "GxP", "Returns Quality", "Safety"] },
  { key: "organization_operations", label: "Organization & Operations", sub: ["Governance", "Operating Model", "Site Mgmt"] },
  { key: "future_transformation", label: "Future Transformation & Innovation", sub: ["Roadmap", "AI / ML", "Sustainability"] },
];

const PROCESS_LABEL = Object.fromEntries(BUSINESS_PROCESSES.map((p) => [p.key, p.label]));
const PROCESS_SUBS = Object.fromEntries(BUSINESS_PROCESSES.map((p) => [p.key, p.sub]));

// Public UX uses Priority + Impact Area (Gilead-style). Values are kept
// in lower-case so the backend can map `priority → severity` 1:1 without
// translation tables.
const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];
const PRIORITY_CHIP = {
  low: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  medium: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  high: "bg-[#FED7AA] text-[#9A3412] border-[#FDBA74]",
  critical: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};
const IMPACT_AREA_OPTIONS = [
  "Efficiency",
  "Productivity",
  "Quality & Compliance",
  "Safety",
  "Inventory Accuracy",
  "Cost Reduction",
  "Service Level",
  "Operational Visibility",
  "Standardization",
];

const ENGAGEMENT_TYPES = [
  { value: "workshop", label: "Workshop" },
  { value: "remote_review", label: "Remote Review" },
  { value: "post_go_live", label: "Post-Go-Live" },
  { value: "optimization", label: "Optimization" },
];

const INDUSTRIES = SHARED_INDUSTRIES;
const STRATEGIC_OBJECTIVES = [
  "Throughput Improvement", "Cost Reduction", "Inventory Accuracy",
  "Multi-Site Standardization", "Automation Enablement",
  "Digital Transformation", "Service-Level Improvement",
];
const BUSINESS_MODELS = ["B2B Distribution", "B2C Fulfillment", "Manufacturing", "3PL", "Hybrid"];
const GEOGRAPHIC_DISTRIBUTIONS = [
  "Single Country",
  "Multi-Country",
  "Regional Network",
  "Global Network",
];
const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const WAREHOUSE_TYPES = [
  { value: "cdc", label: "Central Distribution Center" },
  { value: "rdc", label: "Regional Distribution Center" },
  { value: "fc", label: "Fulfillment Center" },
  { value: "plant", label: "Plant Warehouse" },
  { value: "3pl", label: "3PL Site" },
  { value: "other", label: "Other" },
];
const SITE_SIZES = ["Small (<10k m²)", "Mid (10–50k m²)", "Large (50–100k m²)", "Mega (>100k m²)"];
const AUTOMATION_MATURITIES = ["Manual", "Low", "Medium", "High", "Lights-Out Vision"];

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// ---------------------------------------------------------------------------
// Small UI primitives
// ---------------------------------------------------------------------------
// SmartImprove inline Stepper — mirrors SmartEvaluate's StepProgress visual
// vocabulary: a small "Step N of M — Label" counter on top, then a single
// row of teal-filled discs joined by a thin connector line. Honest progress
// only (completed / current / upcoming) — no misleading percentage labels.
function Stepper({ step }) {
  return (
    <div
      data-testid="intake-stepper"
      className="mb-6 bg-white border border-[#E5E7EB] rounded-sm px-4 py-3"
    >
      <p
        className="text-sm font-semibold text-[#111827] tracking-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Module {step} — {STEP_TITLE[step] || ""}
      </p>
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#94A3B8] mt-0.5">
        Step {step} of {TOTAL_STEPS}
      </p>
      <div className="mt-3 flex items-center">
        {STEPS.map((s, idx) => {
          const isCompleted = step > s.id;
          const isCurrent = step === s.id;
          const isLast = idx === STEPS.length - 1;
          return (
            <div key={s.id} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div
                  data-testid={`step-indicator-${s.id}`}
                  data-state={
                    isCurrent ? "current" : isCompleted ? "completed" : "upcoming"
                  }
                  className={[
                    "w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 border",
                    isCompleted
                      ? "bg-[#03738C] text-white border-[#03738C] shadow-sm"
                      : isCurrent
                      ? "bg-[#025979] text-white border-[#025979] shadow-sm ring-4 ring-[#025979]/10"
                      : "bg-white text-[#9CA3AF] border-[#D1D5DB]",
                  ].join(" ")}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                  ) : isCurrent ? (
                    <span
                      aria-hidden
                      className="w-1.5 h-1.5 rounded-full bg-white"
                    />
                  ) : (
                    <span className="text-[11px] font-semibold">{s.id}</span>
                  )}
                </div>
                <span
                  className={[
                    "mt-1.5 text-[10px] font-medium tracking-wide uppercase transition-colors hidden sm:inline-block text-center",
                    isCurrent
                      ? "text-[#025979]"
                      : isCompleted
                      ? "text-[#1F2937]"
                      : "text-[#9CA3AF]",
                  ].join(" ")}
                >
                  {s.short || s.label}
                </span>
              </div>
              {!isLast ? (
                <div
                  aria-hidden
                  className={[
                    "flex-1 h-[2px] mx-2 rounded-full transition-colors duration-200",
                    isCompleted ? "bg-[#03738C]" : "bg-[#E5E7EB]",
                  ].join(" ")}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Iter 92: invalid-state border + reusable touched-on-blur hook so all
// SmartImprove modules share a single, consistent inline-validation UX
// (red border + "Required" helper, errors surface only after blur).
function Field({ label, required, error, helper, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#374151] mb-1">
        {label} {required ? <span className="text-[#B91C1C]">*</span> : null}
      </label>
      {children}
      {error ? <p className="text-[10px] text-[#B91C1C] mt-1">{error}</p> :
        helper ? <p className="text-[10px] text-[#94A3B8] mt-1">{helper}</p> :
        required ? <p className="text-[10px] text-[#94A3B8] mt-1">Required</p> : null}
    </div>
  );
}

// Tailwind helpers — apply consistent invalid-state styling to inputs
// across every SmartImprove module form.
const INVALID_CLS = "border-[#B91C1C] focus-visible:ring-[#B91C1C]/30";
const inputCls = (invalid) =>
  `rounded-sm text-xs${invalid ? ` ${INVALID_CLS}` : ""}`;
const triggerCls = (invalid) =>
  `rounded-sm text-xs${invalid ? ` ${INVALID_CLS}` : ""}`;

// useTouched — minimal "field touched-on-blur" tracker so inline errors
// don't yell at the user the moment the form is opened. Components call
// `setTouched(name)` in onBlur and check `touched(name)` before rendering
// an error.
function useTouched() {
  const [touched, setTouched] = useState({});
  return {
    mark: (name) => setTouched((t) => (t[name] ? t : { ...t, [name]: true })),
    markAll: (names) =>
      setTouched((t) => {
        const next = { ...t };
        names.forEach((n) => { next[n] = true; });
        return next;
      }),
    is: (name) => !!touched[name],
  };
}

function Card({ children, className = "", testId }) {
  return (
    <section
      className={`bg-white border border-[#E5E7EB] rounded-sm p-5 ${className}`}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

function IntelligenceHint({ children }) {
  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-[#F0F7FA] border-l-2 border-[#025979] text-[11px] text-[#0F172A] leading-[1.55]">
      <Info className="w-3.5 h-3.5 text-[#025979] mt-0.5 shrink-0" />
      <p>{children}</p>
    </div>
  );
}

function ChipSelect({ value, onChange, options, testId, cols = "auto" }) {
  // SmartEvaluate-aligned grid: tighter spacing + equal-height pills + no
  // loose horizontal wrap. `cols` resolves to a responsive grid template
  // tuned for short / medium / long option lists.
  const gridCls =
    cols === 3
      ? "grid grid-cols-3 gap-2"
      : cols === 4
      ? "grid grid-cols-2 sm:grid-cols-4 gap-2"
      : cols === 5
      ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
      : cols === 6
      ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
      : // auto — wide chip grid that wraps without giant gaps
        "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2";
  return (
    <div className={gridCls} data-testid={testId}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={active}
            data-state={active ? "selected" : "unselected"}
            className={[
              "px-3 py-2 text-xs font-medium rounded-sm border transition-all duration-150 text-center leading-tight min-h-[36px] flex items-center justify-center",
              active
                ? "bg-[#025979] text-white border-[#025979] shadow-sm"
                : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#025979] hover:bg-[#F0F7FA]",
            ].join(" ")}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Client Details
// ---------------------------------------------------------------------------
function Step1ClientDetails({ value, onChange, onSubmit, submitting, onDemoApplied }) {
  const t = useTouched();
  // Iter 92 — tighter validation: trim + email format + phone sanity.
  // Phone: allow digits, spaces, +, -, (, ), . — reject anything else
  // (no heavy international parsing — that's Phase 2+).
  const PHONE_RE = /^[\d+\-()\s.]+$/;
  const errs = {
    company_name: !value.company_name.trim() ? "Required" : "",
    contact_name: !value.contact_name.trim() ? "Required" : "",
    email: !value.email.trim()
      ? "Required"
      : !isValidEmail(value.email)
      ? "Enter a valid email"
      : "",
    phone:
      value.phone && value.phone.trim() && !PHONE_RE.test(value.phone.trim())
        ? "Use digits, spaces and + - ( ) . only"
        : "",
  };
  const valid =
    !errs.company_name && !errs.contact_name && !errs.email && !errs.phone;
  const handleSubmit = () => {
    if (!valid) {
      t.markAll(["company_name", "contact_name", "email", "phone"]);
      return;
    }
    onSubmit();
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#94A3B8]">
            Step 1 of 3
          </p>
          <h2
            className="text-xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Module 1 — Client &amp; Engagement Details
          </h2>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Tell us who you are. We use these details to assign the right
            consulting specialist and to send you confirmation of your
            submission.
          </p>
        </div>
        <SmartImproveLoadDemoButton variant="full" onApplied={onDemoApplied} />
      </div>

      <Card testId="step1-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="Company name"
            required
            error={t.is("company_name") ? errs.company_name : ""}
          >
            <Input
              value={value.company_name}
              onChange={(e) => onChange({ company_name: e.target.value })}
              onBlur={() => t.mark("company_name")}
              placeholder="e.g. Acme Logistics International"
              className={inputCls(t.is("company_name") && !!errs.company_name)}
              data-testid="step1-company"
            />
          </Field>
          <Field
            label="Contact name"
            required
            error={t.is("contact_name") ? errs.contact_name : ""}
          >
            <Input
              value={value.contact_name}
              onChange={(e) => onChange({ contact_name: e.target.value })}
              onBlur={() => t.mark("contact_name")}
              placeholder="e.g. Jane Doe"
              className={inputCls(t.is("contact_name") && !!errs.contact_name)}
              data-testid="step1-contact"
            />
          </Field>
          <Field
            label="Contact email"
            required
            error={t.is("email") ? errs.email : ""}
          >
            <Input
              type="email"
              value={value.email}
              onChange={(e) => onChange({ email: e.target.value })}
              onBlur={() => t.mark("email")}
              placeholder="name@company.com"
              className={inputCls(t.is("email") && !!errs.email)}
              data-testid="step1-email"
            />
          </Field>
          <Field
            label="Phone number"
            error={t.is("phone") ? errs.phone : ""}
            helper={!errs.phone ? "Optional — digits, spaces, + - ( ) ." : ""}
          >
            <Input
              value={value.phone}
              onChange={(e) => onChange({ phone: e.target.value })}
              onBlur={() => t.mark("phone")}
              placeholder="+1 555 0100"
              className={inputCls(t.is("phone") && !!errs.phone)}
              data-testid="step1-phone"
            />
          </Field>
          <Field label="Job title / role" helper="Optional">
            <Input
              value={value.role}
              onChange={(e) => onChange({ role: e.target.value })}
              placeholder="e.g. Head of Warehouse Operations"
              className="rounded-sm text-xs"
              data-testid="step1-role"
            />
          </Field>
          <Field
            label="Assigned consultant"
            helper="Optional internal field — leave blank if not assigned yet."
          >
            <Input
              value={value.assigned_consultant}
              onChange={(e) =>
                onChange({ assigned_consultant: e.target.value })
              }
              placeholder="e.g. priya@warehousex.com"
              className="rounded-sm text-xs"
              data-testid="step1-consultant"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes" helper="Optional — anything we should know before kickoff.">
              <Textarea
                value={value.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
                rows={3}
                placeholder="Strategic context, expected timeline, sponsorship details…"
                className="rounded-sm text-xs"
                data-testid="step1-notes"
              />
            </Field>
          </div>
        </div>
      </Card>

      <IntelligenceHint>
        These details establish the engagement record. After Step 1 saves, you
        can resume this assessment over multiple workshop sessions using the
        link emailed to you.
      </IntelligenceHint>

      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
          data-testid="step1-submit"
        >
          {submitting ? "Saving…" : "Save & Continue to Business & Warehouse Scope"}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Business & Warehouse Scope
// ---------------------------------------------------------------------------
// Warehouse Operational Baseline — shared helpers (June 2026). USD-only.
// Capture/display only; NOT wired to any ROI engine.
const obHas = (v) => v !== null && v !== undefined && v !== "";
const obCompact = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return String(n ?? "");
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(v % 1e9 === 0 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(v % 1e3 === 0 ? 0 : 1)}K`;
  return String(v);
};
const obLaborLabel = (s) => {
  if (s?.use_labor_cost_benchmark) return "PrimeS4 benchmark";
  if (obHas(s?.labor_cost_per_hour_usd))
    return `USD ${Number(s.labor_cost_per_hour_usd).toLocaleString()}/hr`;
  return "Not provided";
};
const obInvLabel = (s) => {
  if (obHas(s?.inventory_value_usd)) return `USD ${obCompact(s.inventory_value_usd)}`;
  return "Not provided";
};

function WarehouseDialog({ open, onOpenChange, value, onChange, onSave, saving }) {
  const isEdit = !!value?.id;
  const set = (patch) => onChange({ ...value, ...patch });
  const t = useTouched();
  // Iter 92 — required-field validation for the Warehouse modal so the
  // engagement carries a minimum operational landscape per site.
  const errs = {
    warehouse_name: !(value?.warehouse_name || "").trim() ? "Required" : "",
    country: !(value?.country || "").trim() ? "Required" : "",
    warehouse_type: !value?.warehouse_type ? "Required" : "",
    sap_landscape: !(value?.sap_landscape || "").trim() ? "Required" : "",
    ewm_version: !(value?.ewm_version || "").trim() ? "Required" : "",
    site_size: !value?.site_size ? "Required" : "",
    workforce_count:
      !isEdit && !String(value?.workforce_count ?? "").trim() ? "Required" : "",
  };
  const allValid = !Object.values(errs).some(Boolean);
  const handleSave = () => {
    if (!allValid) {
      t.markAll(Object.keys(errs));
      return;
    }
    onSave();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="warehouse-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Warehouse" : "Add Warehouse"}
          </DialogTitle>
          <DialogDescription>
            Capture one warehouse in the SmartImprove engagement scope.
            Duplicate later if multiple warehouses share the same landscape.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <Field
            label="Warehouse name"
            required
            error={t.is("warehouse_name") ? errs.warehouse_name : ""}
          >
            <Input
              value={value?.warehouse_name || ""}
              onChange={(e) => set({ warehouse_name: e.target.value })}
              onBlur={() => t.mark("warehouse_name")}
              placeholder="e.g. DC-EU1"
              className={inputCls(t.is("warehouse_name") && !!errs.warehouse_name)}
              data-testid="warehouse-name"
            />
          </Field>
          <Field
            label="Country / region"
            required
            error={t.is("country") ? errs.country : ""}
          >
            <Input
              value={value?.country || ""}
              onChange={(e) => set({ country: e.target.value })}
              onBlur={() => t.mark("country")}
              placeholder="e.g. Netherlands"
              className={inputCls(t.is("country") && !!errs.country)}
              data-testid="warehouse-country"
            />
          </Field>
          <Field
            label="Warehouse type"
            required
            error={t.is("warehouse_type") ? errs.warehouse_type : ""}
          >
            <Select
              value={value?.warehouse_type || ""}
              onValueChange={(v) => { set({ warehouse_type: v }); t.mark("warehouse_type"); }}
            >
              <SelectTrigger
                className={triggerCls(t.is("warehouse_type") && !!errs.warehouse_type)}
                data-testid="warehouse-type"
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {WAREHOUSE_TYPES.map((t2) => (
                  <SelectItem key={t2.value} value={t2.value}>
                    {t2.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field
            label="SAP landscape"
            required
            error={t.is("sap_landscape") ? errs.sap_landscape : ""}
          >
            <Input
              value={value?.sap_landscape || ""}
              onChange={(e) => set({ sap_landscape: e.target.value })}
              onBlur={() => t.mark("sap_landscape")}
              placeholder="e.g. SAP S/4HANA 2023"
              className={inputCls(t.is("sap_landscape") && !!errs.sap_landscape)}
              data-testid="warehouse-sap"
            />
          </Field>
          <Field
            label="WMS / EWM landscape"
            required
            error={t.is("ewm_version") ? errs.ewm_version : ""}
          >
            <Input
              value={value?.ewm_version || ""}
              onChange={(e) => set({ ewm_version: e.target.value })}
              onBlur={() => t.mark("ewm_version")}
              placeholder="e.g. SAP EWM 2024 / Decentralized"
              className={inputCls(t.is("ewm_version") && !!errs.ewm_version)}
              data-testid="warehouse-ewm"
            />
          </Field>
          <Field
            label="Site size"
            required
            error={t.is("site_size") ? errs.site_size : ""}
          >
            <Select
              value={value?.site_size || ""}
              onValueChange={(v) => { set({ site_size: v }); t.mark("site_size"); }}
            >
              <SelectTrigger
                className={triggerCls(t.is("site_size") && !!errs.site_size)}
                data-testid="warehouse-size"
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {SITE_SIZES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Automation maturity" helper="Optional">
            <Select
              value={value?.automation_maturity || ""}
              onValueChange={(v) => set({ automation_maturity: v })}
            >
              <SelectTrigger className="rounded-sm text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {AUTOMATION_MATURITIES.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2 border-t border-[#E5E7EB] mt-1 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">
              Operational Baseline
            </p>
            <p className="text-[10px] text-[#94A3B8] mb-2.5">
              Used to estimate improvement value. Approximate values are fine.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Warehouse workforce"
                required={!isEdit}
                error={t.is("workforce_count") ? errs.workforce_count : ""}
                helper="Approx. operational employees"
              >
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={value?.workforce_count ?? ""}
                  onChange={(e) => set({ workforce_count: e.target.value })}
                  onBlur={() => t.mark("workforce_count")}
                  placeholder="e.g. 35"
                  className={inputCls(t.is("workforce_count") && !!errs.workforce_count)}
                  data-testid="warehouse-workforce"
                />
              </Field>
              <Field label="Operating shifts / day" helper="Optional · typically 1–3">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={value?.shifts_per_day ?? ""}
                  onChange={(e) => set({ shifts_per_day: e.target.value })}
                  placeholder="e.g. 2"
                  className="rounded-sm text-xs"
                  data-testid="warehouse-shifts"
                />
              </Field>
              <Field label="Annual inbound deliveries" helper="Optional · approximate">
                <Input
                  type="number"
                  min="0"
                  value={value?.annual_inbound_deliveries ?? ""}
                  onChange={(e) => set({ annual_inbound_deliveries: e.target.value })}
                  placeholder="e.g. 100,000"
                  className="rounded-sm text-xs"
                  data-testid="warehouse-inbound"
                />
              </Field>
              <Field label="Annual outbound orders" helper="Optional · approximate">
                <Input
                  type="number"
                  min="0"
                  value={value?.annual_outbound_orders ?? ""}
                  onChange={(e) => set({ annual_outbound_orders: e.target.value })}
                  placeholder="e.g. 250,000"
                  className="rounded-sm text-xs"
                  data-testid="warehouse-outbound"
                />
              </Field>
              <Field label="Average loaded labor cost / hour (USD)" helper="Optional">
                <Input
                  type="number"
                  min="0"
                  disabled={!!value?.use_labor_cost_benchmark}
                  value={value?.use_labor_cost_benchmark ? "" : (value?.labor_cost_per_hour_usd ?? "")}
                  onChange={(e) => set({ labor_cost_per_hour_usd: e.target.value })}
                  placeholder={value?.use_labor_cost_benchmark ? "PrimeS4 benchmark" : "e.g. 28"}
                  className="rounded-sm text-xs"
                  data-testid="warehouse-labor-cost"
                />
                <label className="flex items-center gap-1.5 mt-1.5 text-[10px] text-[#374151] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!value?.use_labor_cost_benchmark}
                    onChange={(e) =>
                      set({
                        use_labor_cost_benchmark: e.target.checked,
                        labor_cost_per_hour_usd: e.target.checked ? "" : (value?.labor_cost_per_hour_usd ?? ""),
                      })
                    }
                    data-testid="warehouse-labor-benchmark"
                  />
                  Use PrimeS4 benchmark
                </label>
              </Field>
              <Field label="Approx. inventory value (USD)" helper="Optional">
                <Input
                  type="number"
                  min="0"
                  value={value?.inventory_value_usd ?? ""}
                  onChange={(e) => set({ inventory_value_usd: e.target.value })}
                  placeholder="e.g. 10,000,000"
                  className="rounded-sm text-xs"
                  data-testid="warehouse-inventory-value"
                />
              </Field>
            </div>
          </div>
          <div className="md:col-span-2">
            <Field label="Operational notes" helper="Optional">
              <Textarea
                value={value?.notes || ""}
                onChange={(e) => set({ notes: e.target.value })}
                rows={3}
                placeholder="Throughput, peak shifts, known constraints…"
                className="rounded-sm text-xs"
              />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !allValid}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
            data-testid="warehouse-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add warehouse"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WarehouseCard({ site, onEdit, onDuplicate, onDelete }) {
  const [open, setOpen] = useState(false);
  const typeLabel = WAREHOUSE_TYPES.find((t) => t.value === site.warehouse_type)?.label;
  return (
    <div
      className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden"
      data-testid={`warehouse-card-${site.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#F8FAFC] text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Warehouse className="w-4 h-4 text-[#025979] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#111827] truncate">
              {site.warehouse_name}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {typeLabel ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]">
                  {typeLabel}
                </span>
              ) : null}
              {site.country ? (
                <span className="text-[10px] text-[#6B7280]">{site.country}</span>
              ) : null}
              {site.sap_landscape ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
                  {site.sap_landscape}
                </span>
              ) : null}
              {site.ewm_version ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
                  {site.ewm_version}
                </span>
              ) : null}
              {obHas(site.workforce_count) ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
                  {site.workforce_count} staff
                </span>
              ) : null}
              {obHas(site.shifts_per_day) ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
                  {site.shifts_per_day} shift{Number(site.shifts_per_day) === 1 ? "" : "s"}
                </span>
              ) : null}
              {obHas(site.annual_inbound_deliveries) ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
                  {obCompact(site.annual_inbound_deliveries)} inbound
                </span>
              ) : null}
              {obHas(site.annual_outbound_orders) ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
                  {obCompact(site.annual_outbound_orders)} outbound
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(site); }}
            className="p-1.5 text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
            aria-label="Edit"
            data-testid={`warehouse-edit-${site.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDuplicate(site); }}
            className="p-1.5 text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
            aria-label="Duplicate"
            data-testid={`warehouse-duplicate-${site.id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(site); }}
            className="p-1.5 text-[#B91C1C] hover:bg-[#FEF2F2] rounded-sm"
            aria-label="Delete"
            data-testid={`warehouse-delete-${site.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {open ? (
            <ChevronDown className="w-4 h-4 text-[#6B7280] ml-1" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#6B7280] ml-1" />
          )}
        </div>
      </button>
      {open ? (
        <div className="px-4 pb-4 pt-1 border-t border-[#F1F5F9] grid grid-cols-2 md:grid-cols-3 gap-3 text-[11px]">
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Site size</p><p className="text-[#111827]">{site.site_size || "—"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Automation maturity</p><p className="text-[#111827]">{site.automation_maturity || "—"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Country</p><p className="text-[#111827]">{site.country || "—"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Warehouse workforce</p><p className="text-[#111827]">{obHas(site.workforce_count) ? site.workforce_count : "Not provided"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Operating shifts / day</p><p className="text-[#111827]">{obHas(site.shifts_per_day) ? site.shifts_per_day : "Not provided"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Annual inbound deliveries</p><p className="text-[#111827]">{obHas(site.annual_inbound_deliveries) ? Number(site.annual_inbound_deliveries).toLocaleString() : "Not provided"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Annual outbound orders</p><p className="text-[#111827]">{obHas(site.annual_outbound_orders) ? Number(site.annual_outbound_orders).toLocaleString() : "Not provided"}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Loaded labor cost / hr (USD)</p><p className="text-[#111827]">{obLaborLabel(site)}</p></div>
          <div><p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Inventory value (USD)</p><p className="text-[#111827]">{obInvLabel(site)}</p></div>
          {site.notes ? (
            <div className="col-span-2 md:col-span-3">
              <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Notes</p>
              <p className="text-[#374151]">{site.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Step2BusinessWarehouse({
  ctx, sites, savingCtx, onCtxChange, onCtxSave,
  onAddWarehouse, onEditWarehouse, onDuplicateWarehouse, onDeleteWarehouse,
  onBack, onContinue,
}) {
  const t = useTouched();
  // Iter 92 — required-field validation surface for Module 2. Errors
  // only render after the user touches a control OR clicks Continue.
  const errs = {
    industry: !ctx.industry ? "Select an industry" : "",
    engagement_type: !ctx.engagement_type ? "Select an engagement type" : "",
    strategic_objective: !ctx.strategic_objective ? "Select a strategic objective" : "",
    geographic_distribution: !ctx.geographic_distribution ? "Select geographic distribution" : "",
    business_model: !ctx.business_model ? "Select a primary business model" : "",
    priority: !ctx.priority ? "Set engagement priority" : "",
    num_warehouse_sites:
      !ctx.num_warehouse_sites || Number(ctx.num_warehouse_sites) < 1
        ? "Enter the planned number of sites"
        : "",
  };
  const valid =
    !Object.values(errs).some(Boolean) && sites.length >= 1;
  const handleContinue = () => {
    if (!valid) {
      t.markAll(Object.keys(errs));
      t.mark("warehouses");
      return;
    }
    onContinue();
  };
  const warehousesError =
    (t.is("warehouses") || sites.length === 0) && sites.length === 0
      ? "At least one warehouse must be added."
      : "";
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#94A3B8]">
          Step 2 of 3
        </p>
        <h2
          className="text-xl font-bold text-[#111827] mt-1"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Module 2 — Business &amp; Warehouse Scope
        </h2>
        <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
          Capture the strategic context for the engagement and the warehouse
          sites in scope.
        </p>
      </div>

      <Card testId="step2-section-a">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#F1F5F9]">
          <Briefcase className="w-4 h-4 text-[#025979]" />
          <h3 className="text-xs font-semibold text-[#1F2937] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
            Engagement Context
          </h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Industry"
              required
              error={t.is("industry") ? errs.industry : ""}
              helper={!errs.industry ? "What sector does the customer operate in?" : ""}
            >
              <Select
                value={ctx.industry || ""}
                onValueChange={(v) => { onCtxChange({ industry: v }); t.mark("industry"); }}
              >
                <SelectTrigger
                  className={triggerCls(t.is("industry") && !!errs.industry)}
                  data-testid="ctx-industry"
                >
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Engagement type"
              required
              error={t.is("engagement_type") ? errs.engagement_type : ""}
            >
              <ChipSelect
                value={ctx.engagement_type}
                onChange={(v) => { onCtxChange({ engagement_type: v }); t.mark("engagement_type"); }}
                options={ENGAGEMENT_TYPES}
                cols={4}
              />
            </Field>
          </div>
          <Field
            label="Strategic objective"
            required
            error={t.is("strategic_objective") ? errs.strategic_objective : ""}
            helper={!errs.strategic_objective ? "The primary outcome this engagement targets." : ""}
          >
            <ChipSelect
              value={ctx.strategic_objective}
              onChange={(v) => { onCtxChange({ strategic_objective: v }); t.mark("strategic_objective"); }}
              options={STRATEGIC_OBJECTIVES}
              cols="auto"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field
              label="Geographic distribution"
              required
              error={t.is("geographic_distribution") ? errs.geographic_distribution : ""}
              helper={!errs.geographic_distribution ? "Where are the warehouses in scope located?" : ""}
            >
              <Select
                value={ctx.geographic_distribution || ""}
                onValueChange={(v) => { onCtxChange({ geographic_distribution: v }); t.mark("geographic_distribution"); }}
              >
                <SelectTrigger className={triggerCls(t.is("geographic_distribution") && !!errs.geographic_distribution)}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {GEOGRAPHIC_DISTRIBUTIONS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Primary business model"
              required
              error={t.is("business_model") ? errs.business_model : ""}
            >
              <Select
                value={ctx.business_model || ""}
                onValueChange={(v) => { onCtxChange({ business_model: v }); t.mark("business_model"); }}
              >
                <SelectTrigger className={triggerCls(t.is("business_model") && !!errs.business_model)}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Priority"
              required
              error={t.is("priority") ? errs.priority : ""}
            >
              <ChipSelect
                value={ctx.priority}
                onChange={(v) => { onCtxChange({ priority: v }); t.mark("priority"); }}
                options={PRIORITIES}
                cols={3}
              />
            </Field>
          </div>
          <Field
            label="Number of warehouse sites"
            required
            error={t.is("num_warehouse_sites") ? errs.num_warehouse_sites : ""}
            helper={!errs.num_warehouse_sites ? "Add each site individually below — we use this to size the engagement." : ""}
          >
            <Input
              type="number"
              min={1}
              value={ctx.num_warehouse_sites || ""}
              onChange={(e) => onCtxChange({ num_warehouse_sites: Number(e.target.value) || "" })}
              onBlur={() => t.mark("num_warehouse_sites")}
              placeholder="e.g. 5"
              className={`${inputCls(t.is("num_warehouse_sites") && !!errs.num_warehouse_sites)} w-32`}
              data-testid="ctx-num-sites"
            />
          </Field>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={onCtxSave}
              disabled={savingCtx}
              className="rounded-sm text-xs h-9 px-4 border-[#025979] text-[#025979]"
              data-testid="ctx-save"
            >
              {savingCtx ? "Saving…" : "Save context"}
            </Button>
          </div>
        </div>
      </Card>

      <Card testId="step2-section-b">
        <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-[#F1F5F9]">
          <div className="flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-[#025979]" />
            <h3 className="text-xs font-semibold text-[#1F2937] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
              Warehouses in Scope <span className="text-[#B91C1C]">*</span>
            </h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569] ml-1">
              {sites.length}
            </span>
          </div>
          <Button
            onClick={onAddWarehouse}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
            data-testid="warehouse-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Warehouse
          </Button>
        </div>
        {sites.length === 0 ? (
          <div
            className={[
              "border border-dashed rounded-sm p-8 text-center",
              warehousesError ? "border-[#B91C1C] bg-[#FEF2F2]" : "border-[#E5E7EB] bg-[#FAFBFC]",
            ].join(" ")}
            data-testid="warehouse-empty-state"
          >
            <Building2 className={`w-6 h-6 mx-auto ${warehousesError ? "text-[#B91C1C]" : "text-[#94A3B8]"}`} />
            <p className={`text-xs mt-2 ${warehousesError ? "text-[#B91C1C] font-medium" : "text-[#6B7280]"}`}>
              {warehousesError
                ? warehousesError
                : <>No warehouses added yet. Click <strong>Add Warehouse</strong> to capture the first site in scope.</>
              }
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sites.map((s) => (
              <WarehouseCard
                key={s.id}
                site={s}
                onEdit={onEditWarehouse}
                onDuplicate={onDuplicateWarehouse}
                onDelete={onDeleteWarehouse}
              />
            ))}
          </div>
        )}
      </Card>

      <IntelligenceHint>
        Warehouses captured here become available in Step 3 for finding-level
        applicability, KPI scope, and downstream ROI / implementation planning.
      </IntelligenceHint>

      <div className="flex justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          className="rounded-sm text-xs h-9 px-4"
          data-testid="step2-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={sites.length === 0}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
          data-testid="step2-continue"
        >
          Save & Continue to Operational Findings Assessment
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Operational Assessment
// ---------------------------------------------------------------------------
// Helper — find the canonical sub_process_key for a given (process, label)
// pair against the taxonomy returned by the public catalogue endpoint.
function lookupSubKey(processes, processKey, subLabel) {
  if (!processKey || !subLabel) return "";
  const proc = (processes || []).find((p) => p.key === processKey);
  const match = (proc?.sub_processes || []).find(
    (s) => s.label === subLabel || s.key === subLabel
  );
  return match?.key || "";
}

// Iter 92 — visual grouping wrapper used inside the Finding modal so each
// of the 4 logical sections (Operational Issue · Business Impact · Root
// Cause & Workaround · KPI Impact) reads as a discrete card with a small
// eyebrow numeral. Pure presentational — no state.
function FindingSection({ title, eyebrow, children }) {
  return (
    <div className="border border-[#E5E7EB] rounded-sm p-3 bg-white">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#F1F5F9]">
        <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#94A3B8]">
          {eyebrow}
        </span>
        <h4
          className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h4>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FindingDialog({
  open, onOpenChange, value, onChange, onSave, saving, sites,
  processes, issueTemplates, kpiTemplates, templatesLoading,
}) {
  const isEdit = !!value?.id;
  const set = (p) => onChange({ ...value, ...p });
  const procEntry = (processes || []).find(
    (p) => p.key === value?.operational_area
  );
  const subOptions = procEntry?.sub_processes || [];
  // Iter 92 — KPI section starts collapsed (optional + visual-only). Auto-
  // expand if the finding being edited already carries KPI data.
  const initialKpiOpen = !!(value?.kpi_metric_label || value?.kpi_unit ||
    value?.kpi_current_value != null || value?.kpi_target_value != null);
  const [kpiOpen, setKpiOpen] = useState(initialKpiOpen);
  useEffect(() => { if (open) setKpiOpen(initialKpiOpen); }, [open, initialKpiOpen]);
  const toggleSite = (sid) => {
    const cur = value?.applicable_site_ids || [];
    set({
      applicable_site_ids: cur.includes(sid)
        ? cur.filter((x) => x !== sid)
        : [...cur, sid],
    });
  };
  const applyIssueTemplate = (tpl) => {
    if (!tpl) return;
    set({
      title: tpl.title || value?.title || "",
      description: tpl.description || value?.description || "",
      // Library templates use the legacy `severity` vocabulary — map it
      // 1:1 onto the public `priority` field so the user-facing chip in
      // the form reflects the template's recommendation.
      priority: tpl.severity || value?.priority || "medium",
      observation_notes:
        tpl.typical_symptoms || value?.observation_notes || "",
      // Tag the finding so the consultant can see it came from the library.
      // Backend Finding model already accepts `issue_template_source`.
      issue_template_source: "library",
      issue_template_slug: tpl.slug || tpl.id || "",
    });
  };
  const applyKpiTemplate = (tpl) => {
    if (!tpl) return;
    // Map backend trend_direction → SmartImprove kpi_direction vocabulary.
    let direction = value?.kpi_direction || "increase";
    if (tpl.improvement_direction === "reduce") direction = "decrease";
    else if (tpl.improvement_direction === "increase") direction = "increase";
    else if (tpl.trend_direction === "lower_is_better") direction = "decrease";
    else if (tpl.trend_direction === "higher_is_better") direction = "increase";
    set({
      kpi_metric_key: tpl.slug || tpl.id || "",
      kpi_metric_label: tpl.name || "",
      kpi_unit: tpl.unit || "",
      kpi_direction: direction,
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="finding-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Operational Finding" : "Add Operational Finding"}
          </DialogTitle>
          <DialogDescription>
            One finding represents one operational problem — process
            inefficiency, observation, KPI gap, or recurring execution issue.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto space-y-3 pr-1 py-2">
          {/* ─── Section 1 · Operational Issue ─────────────────────────── */}
          <FindingSection title="Operational Issue" eyebrow="1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Business process" required>
              <Select
                value={value?.operational_area || ""}
                onValueChange={(v) =>
                  set({
                    operational_area: v,
                    sub_process: "",
                    // Drop any template linkage when scope changes.
                    issue_template_source: "manual",
                    issue_template_slug: "",
                  })
                }
              >
                <SelectTrigger className="rounded-sm text-xs" data-testid="finding-process">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {(processes || []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sub-process" required>
              <Select
                value={value?.sub_process || ""}
                onValueChange={(v) =>
                  set({
                    sub_process: v,
                    issue_template_source: "manual",
                    issue_template_slug: "",
                  })
                }
                disabled={!subOptions.length}
              >
                <SelectTrigger className="rounded-sm text-xs" data-testid="finding-subprocess">
                  <SelectValue
                    placeholder={
                      subOptions.length ? "Select" : "Select a process first"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {subOptions.map((s) => (
                    <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* PRIVA Suggested Operational Issues — surfaces once a sub-process
              is picked. The backend taxonomy is unchanged ("Issue Templates"
              in admin); this is purely the consultant-facing AI label. */}
          {value?.operational_area && value?.sub_process ? (
            <div
              className="border border-[#DDD6FE] rounded-sm p-3 bg-[#F0EDF9]"
              data-testid="issue-template-section"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#6E5BCE] shrink-0" strokeWidth={1.75} />
                    <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6E5BCE]">
                      PRIVA Suggested Operational Issues
                    </h4>
                  </div>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">
                    Common issue patterns identified for this sub-process · Powered by PRIVA
                  </p>
                </div>
                {value?.issue_template_source === "library" &&
                value?.issue_template_slug ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0EDF9] text-[#473F8B] border border-[#DDD6FE] whitespace-nowrap">
                    PRIVA suggested
                  </span>
                ) : null}
              </div>
              {templatesLoading ? (
                <p className="text-[11px] text-[#6B7280] italic py-2">
                  PRIVA is identifying common patterns…
                </p>
              ) : (issueTemplates || []).length === 0 ? (
                <p
                  className="text-[11px] text-[#6B7280] italic py-2"
                  data-testid="issue-template-empty"
                >
                  PRIVA has no curated patterns for this sub-process yet — enter your finding manually below.
                </p>
              ) : (
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-2"
                  data-testid="issue-template-list"
                >
                  {(issueTemplates || []).map((t) => {
                    const active =
                      value?.issue_template_slug &&
                      (value?.issue_template_slug === t.slug ||
                        value?.issue_template_slug === t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => applyIssueTemplate(t)}
                        data-testid={`issue-template-${t.slug || t.id}`}
                        className={[
                          "text-left p-2.5 rounded-sm border transition-all duration-150",
                          active
                            ? "border-[#6E5BCE] bg-[#F0EDF9] shadow-sm"
                            : "border-[#E5E7EB] bg-white hover:border-[#6E5BCE] hover:bg-[#F0EDF9]",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs font-semibold text-[#111827] leading-tight">
                            {t.title}
                          </p>
                          <span
                            className={[
                              "text-[9px] px-1.5 py-0.5 rounded-sm border uppercase shrink-0",
                              PRIORITY_CHIP[t.severity] || "",
                            ].join(" ")}
                          >
                            {t.severity}
                          </span>
                        </div>
                        {t.description ? (
                          <p className="text-[10px] text-[#6B7280] mt-1 line-clamp-2 leading-snug">
                            {t.description}
                          </p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <Field label="Issue title" required>
            <Input
              value={value?.title || ""}
              onChange={(e) =>
                set({
                  title: e.target.value,
                  // Editing detaches from the library template.
                  issue_template_source: "manual",
                })
              }
              placeholder="e.g. Outbound wave release delays at peak"
              className="rounded-sm text-xs"
              data-testid="finding-title"
            />
          </Field>
          <Field label="Description" required>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              placeholder="What is happening operationally? Be specific."
              className="rounded-sm text-xs"
            />
          </Field>
          </FindingSection>

          {/* ─── Section 2 · Business Impact ────────────────────────────── */}
          <FindingSection title="Business Impact" eyebrow="2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Priority" required>
              <ChipSelect
                value={value?.priority || "medium"}
                onChange={(v) => set({ priority: v })}
                options={PRIORITY_OPTIONS}
                cols={4}
                testId="finding-priority"
              />
            </Field>
            <Field
              label="Frequency / volume"
              helper="e.g. 15 delayed trucks/day, 200 exceptions/week"
            >
              <Input
                value={value?.frequency_volume || ""}
                onChange={(e) => set({ frequency_volume: e.target.value })}
                placeholder="e.g. 15 delayed trucks/day"
                className="rounded-sm text-xs"
              />
            </Field>
          </div>
          <Field
            label="Impact Area"
            required
            helper="Which dimension of operations does this finding affect?"
          >
            <Select
              value={value?.impact_area || ""}
              onValueChange={(v) => set({ impact_area: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="finding-impact-area"
              >
                <SelectValue placeholder="Select impact area" />
              </SelectTrigger>
              <SelectContent>
                {IMPACT_AREA_OPTIONS.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Applicable Warehouse(s)" required>
            <div className="flex flex-wrap gap-2">
              {sites.length === 0 ? (
                <p className="text-[11px] text-[#94A3B8] italic">
                  Add warehouses in Step 2 first.
                </p>
              ) : (
                sites.map((s) => {
                  const active = (value?.applicable_site_ids || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSite(s.id)}
                      className={[
                        "px-3 py-1.5 text-xs rounded-sm border transition",
                        active
                          ? "bg-[#025979] text-white border-[#025979]"
                          : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#94A3B8]",
                      ].join(" ")}
                    >
                      {s.warehouse_name}
                    </button>
                  );
                })
              )}
            </div>
            {sites.length > 0 && (value?.applicable_site_ids || []).length === 0 ? (
              <p className="text-[10px] text-[#B91C1C] mt-1" data-testid="finding-warehouses-error">
                Select at least one warehouse.
              </p>
            ) : null}
          </Field>
          </FindingSection>

          {/* ─── Section 3 · Root Cause & Workaround ────────────────────── */}
          <FindingSection title="Root Cause & Workaround" eyebrow="3">
          <Field label="Observation notes" helper="Optional">
            <Textarea
              value={value?.observation_notes || ""}
              onChange={(e) => set({ observation_notes: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Root cause notes" helper="Optional">
              <Textarea
                value={value?.root_cause || ""}
                onChange={(e) => set({ root_cause: e.target.value })}
                rows={2}
                className="rounded-sm text-xs"
              />
            </Field>
            <Field label="Existing workaround" helper="Optional">
              <Textarea
                value={value?.existing_workaround || ""}
                onChange={(e) => set({ existing_workaround: e.target.value })}
                rows={2}
                className="rounded-sm text-xs"
              />
            </Field>
          </div>
          </FindingSection>

          {/* ─── Section 4 · KPI Impact (optional, collapsed) ───────────── */}
          <div
            className="border border-[#DDD6FE] rounded-sm bg-[#F0EDF9]"
            data-testid="finding-kpi-section"
          >
            <button
              type="button"
              onClick={() => setKpiOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-[#F0EDF9]"
              data-testid="finding-kpi-toggle"
            >
              <div className="flex items-center gap-2">
                {kpiOpen ? (
                  <ChevronDown className="w-3.5 h-3.5 text-[#6E5BCE]" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-[#6E5BCE]" />
                )}
                <Sparkles className="w-3 h-3 text-[#6E5BCE]" strokeWidth={1.75} />
                <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#94A3B8]">
                  4
                </span>
                <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6E5BCE]">
                  PRIVA KPI Impact
                </h4>
                <span className="text-[10px] text-[#94A3B8] normal-case tracking-normal">
                  (Optional · Powered by PRIVA)
                </span>
              </div>
              <span className="text-[10px] text-[#6B7280]">
                {kpiOpen ? "Hide" : "Add KPI"}
              </span>
            </button>
            {kpiOpen ? (
              <div className="px-3 pb-3 pt-1 border-t border-[#E5E7EB]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="KPI Template"
                helper={
                  (kpiTemplates || []).length === 0
                    ? "No KPI templates linked to this sub-process — leave blank or type a custom KPI label."
                    : "Pick from the curated catalogue, or leave blank."
                }
              >
                <Select
                  value={value?.kpi_metric_key || ""}
                  onValueChange={(slug) => {
                    if (slug === "__none__") {
                      set({
                        kpi_metric_key: "",
                        kpi_metric_label: "",
                        kpi_unit: "",
                      });
                      return;
                    }
                    const tpl = (kpiTemplates || []).find(
                      (k) => (k.slug || k.id) === slug
                    );
                    if (tpl) applyKpiTemplate(tpl);
                  }}
                  disabled={(kpiTemplates || []).length === 0}
                >
                  <SelectTrigger
                    className="rounded-sm text-xs"
                    data-testid="kpi-template-select"
                  >
                    <SelectValue placeholder="Select a KPI" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(kpiTemplates || []).map((k) => (
                      <SelectItem key={k.id} value={k.slug || k.id}>
                        {k.name}
                        {k.unit ? ` (${k.unit})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Unit">
                <Input
                  value={value?.kpi_unit || ""}
                  onChange={(e) => set({ kpi_unit: e.target.value })}
                  placeholder="e.g. minutes, %, ppm"
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Current value">
                <Input
                  type="number"
                  value={value?.kpi_current_value ?? ""}
                  onChange={(e) => set({ kpi_current_value: e.target.value === "" ? null : Number(e.target.value) })}
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Target value">
                <Input
                  type="number"
                  value={value?.kpi_target_value ?? ""}
                  onChange={(e) => set({ kpi_target_value: e.target.value === "" ? null : Number(e.target.value) })}
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Direction">
                <Select
                  value={value?.kpi_direction || "increase"}
                  onValueChange={(v) => set({ kpi_direction: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="increase">Increase is better</SelectItem>
                    <SelectItem value="decrease">Decrease is better</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm text-xs h-9 px-4">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving ||
              !value?.title?.trim() ||
              !value?.description?.trim() ||
              !value?.operational_area ||
              !value?.sub_process ||
              !(value?.impact_area || "").trim() ||
              !(value?.applicable_site_ids || []).length
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
            data-testid="finding-save"
          >
            {saving ? "Saving…" : isEdit ? "Save finding" : "Add finding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProcessAccordion({ procKey, findings, sites, onAdd, onEdit, onDelete }) {
  const [open, setOpen] = useState(true);
  const counts = useMemo(() => {
    const pri = { low: 0, medium: 0, high: 0, critical: 0 };
    const whSet = new Set();
    findings.forEach((f) => {
      const p = f.priority || f.severity || "medium";
      pri[p] = (pri[p] || 0) + 1;
      (f.applicable_site_ids || []).forEach((s) => whSet.add(s));
    });
    return { pri, whCount: whSet.size };
  }, [findings]);
  const siteName = (sid) => sites.find((s) => s.id === sid)?.warehouse_name || "—";
  return (
    <div className="border border-[#E5E7EB] rounded-sm overflow-hidden bg-white" data-testid={`process-accordion-${procKey}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#F8FAFC] text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-[#025979]" /> : <ChevronRight className="w-4 h-4 text-[#025979]" />}
          <Factory className="w-3.5 h-3.5 text-[#025979] shrink-0" />
          <h4 className="text-sm font-semibold text-[#111827]">{PROCESS_LABEL[procKey]}</h4>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
            {findings.length} finding{findings.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280] shrink-0">
          {counts.pri.critical ? <span className="text-[#991B1B]">{counts.pri.critical} crit</span> : null}
          {counts.pri.high ? <span className="text-[#9A3412]">{counts.pri.high} high</span> : null}
          <span>{counts.whCount} site{counts.whCount === 1 ? "" : "s"}</span>
          <Button
            onClick={(e) => { e.stopPropagation(); onAdd(procKey); }}
            size="sm"
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-[11px] h-7 px-2 ml-1"
            data-testid={`process-add-${procKey}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Finding
          </Button>
        </div>
      </button>
      {open ? (
        <div className="border-t border-[#F1F5F9]">
          {findings.length === 0 ? (
            <p className="px-4 py-6 text-center text-[11px] text-[#94A3B8] italic">
              No findings in this process yet.
            </p>
          ) : (
            <ul>
              {findings.map((f) => (
                <li
                  key={f.id}
                  className="px-4 py-3 border-b border-[#F1F5F9] last:border-0 hover:bg-[#FAFBFC]"
                  data-testid={`finding-row-${f.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${PRIORITY_CHIP[f.priority || f.severity] || ""}`}>
                          {f.priority || f.severity}
                        </span>
                        {f.impact_area ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">
                            {f.impact_area}
                          </span>
                        ) : null}
                        {f.sub_process ? (
                          <span className="text-[10px] text-[#6B7280]">{f.sub_process}</span>
                        ) : null}
                        {f.kpi_metric_label ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#CCFBF1] text-[#0F766E] border border-[#99F6E4]">
                            KPI: {f.kpi_metric_label}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-[13px] font-semibold text-[#111827]">{f.title}</p>
                      {f.description ? (
                        <p className="text-[11px] text-[#6B7280] mt-1 line-clamp-2">{f.description}</p>
                      ) : null}
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        {(f.applicable_site_ids || []).slice(0, 4).map((sid) => (
                          <span key={sid} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
                            {siteName(sid)}
                          </span>
                        ))}
                        {(f.applicable_site_ids || []).length > 4 ? (
                          <span className="text-[10px] text-[#94A3B8]">
                            +{(f.applicable_site_ids || []).length - 4} more
                          </span>
                        ) : null}
                        {f.frequency_volume ? (
                          <span className="text-[10px] text-[#94A3B8]">· {f.frequency_volume}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => onEdit(f)}
                        className="p-1.5 text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
                        data-testid={`finding-edit-${f.id}`}
                        aria-label="Edit finding"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(f)}
                        className="p-1.5 text-[#B91C1C] hover:bg-[#FEF2F2] rounded-sm"
                        data-testid={`finding-delete-${f.id}`}
                        aria-label="Delete finding"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Step3OperationalAssessment({ findings, sites, onAddFinding, onEditFinding, onDeleteFinding, onBack, onSubmit, submitting }) {
  const byProc = useMemo(() => {
    const m = {};
    BUSINESS_PROCESSES.forEach((p) => (m[p.key] = []));
    findings.forEach((f) => {
      if (!m[f.operational_area]) m[f.operational_area] = [];
      m[f.operational_area].push(f);
    });
    return m;
  }, [findings]);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#94A3B8]">
          Step 3 of 3
        </p>
        <h2
          className="text-xl font-bold text-[#111827] mt-1"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Module 3 — Operational Findings Assessment
        </h2>
        <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
          Capture operational findings across processes, sub-processes,
          warehouses and KPIs. Add findings repeatedly across the workshop —
          everything saves to your draft.
        </p>
      </div>

      <IntelligenceHint>
        These operational findings help SmartImprove identify transformation
        opportunities, ROI benchmarks, and implementation recommendations.
        Frequency and warehouse applicability improve prioritization accuracy.
      </IntelligenceHint>

      <div className="space-y-2">
        {BUSINESS_PROCESSES.map((p) => (
          <ProcessAccordion
            key={p.key}
            procKey={p.key}
            findings={byProc[p.key] || []}
            sites={sites}
            onAdd={onAddFinding}
            onEdit={onEditFinding}
            onDelete={onDeleteFinding}
          />
        ))}
      </div>

      <div className="flex flex-wrap justify-between gap-3 pt-2">
        <Button variant="ghost" onClick={onBack} className="rounded-sm text-xs h-9 px-4" data-testid="step3-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-[#6B7280]">
            {findings.length} finding{findings.length === 1 ? "" : "s"} captured
          </span>
          <Button
            onClick={onSubmit}
            disabled={submitting || findings.length === 0}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-4"
            data-testid="step3-submit"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Submitting…" : "Submit Operational Assessment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Assessment Submitted
// ---------------------------------------------------------------------------
function Step4Submitted({ submittedAt, onRestart, onBackToSmartImprove }) {
  return (
    <div className="max-w-3xl mx-auto" data-testid="step4-thank-you">
      <Card className="text-center">
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#ECFDF5] text-[#047857]">
            <CheckCircle2 className="w-6 h-6" />
          </span>
        </div>
        <h2
          className="text-2xl font-bold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Assessment Submitted
        </h2>
        <p className="text-sm text-[#6B7280] mt-2 max-w-2xl mx-auto">
          Our warehouse transformation specialists are reviewing your
          operational assessment and will prepare tailored improvement
          recommendations.
        </p>
        {submittedAt ? (
          <p className="text-[10px] text-[#94A3B8] mt-2">
            Submitted {new Date(submittedAt).toLocaleString()}
          </p>
        ) : null}

        <div className="text-left bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-5 mt-6">
          <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979] mb-3">
            What Happens Next
          </h3>
          <ul className="space-y-2 text-xs text-[#374151]">
            {[
              "Review of warehouse operational challenges",
              "KPI and process benchmarking analysis",
              "Identification of improvement opportunities",
              "Transformation roadmap preparation",
              "ROI and implementation planning",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Target className="w-3.5 h-3.5 text-[#025979] mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Button
            onClick={onRestart}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="step4-restart"
          >
            Start New Assessment
          </Button>
          <Button
            variant="outline"
            onClick={onBackToSmartImprove}
            className="rounded-sm text-xs border-[#025979] text-[#025979]"
            data-testid="step4-back"
          >
            Back to SmartImprove
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const EMPTY_FINDING = {
  id: null,
  operational_area: "",
  sub_process: "",
  title: "",
  description: "",
  // Public UX uses `priority` + `impact_area`. `severity` is kept on the
  // payload for backward-compat with older drafts only.
  priority: "medium",
  impact_area: "",
  severity: "medium",
  frequency_volume: "",
  observation_notes: "",
  root_cause: "",
  existing_workaround: "",
  applicable_site_ids: [],
  kpi_metric_key: "",
  kpi_metric_label: "",
  kpi_current_value: null,
  kpi_target_value: null,
  kpi_unit: "",
  kpi_direction: "increase",
  // Phase 4.5 — issue-template linkage so consultants can see which findings
  // originated from the curated library vs custom-entered.
  issue_template_source: "manual",
  issue_template_slug: "",
};

export default function ImproveIntakePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [savingCtx, setSavingCtx] = useState(false);
  const [draft, setDraft] = useState(null); // { id, resume_token }
  const [submittedAt, setSubmittedAt] = useState(null);

  // Step 1 form
  const [client, setClient] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    role: "",
    assigned_consultant: "",
    notes: "",
  });

  // Step 2 ctx + sites
  const [ctx, setCtx] = useState({
    engagement_type: "workshop",
    industry: "",
    strategic_objective: "",
    geographic_distribution: "",
    business_model: "",
    num_warehouse_sites: "",
    priority: "medium",
  });
  const [sites, setSites] = useState([]);
  const [whDialog, setWhDialog] = useState(false);
  const [whForm, setWhForm] = useState(null);
  const [savingWh, setSavingWh] = useState(false);

  // Step 3 findings
  const [findings, setFindings] = useState([]);
  const [fDialog, setFDialog] = useState(false);
  const [fForm, setFForm] = useState(null);
  const [savingF, setSavingF] = useState(false);

  // -------------------------------------------------------------------------
  // Demo prefill mode (Feb 2026)
  // -------------------------------------------------------------------------
  // When an admin/demo user loads a SmartImprove Demo Profile we prefill
  // every step's state without writing to the backend. The user reviews +
  // edits through Steps 1/2/3, and only the final "Submit & Create
  // Engagement" call POSTs to /demo-profiles/apply-from-payload to create
  // engagement + sites + findings atomically.
  const [demoMode, setDemoMode] = useState(false);
  const [demoProfile, setDemoProfile] = useState(null);

  const applyDemoProfileToState = (profile) => {
    if (!profile) return;
    // Step 1 — client details. Generate a demo email so the Step 1 validator
    // (which requires a valid email) doesn't block the Next button. The user
    // can overwrite it before clicking through.
    const slug = (profile.profile_name || "demo")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .slice(0, 40);
    const autoEmail = `demo+${slug}-${(profile.id || "").slice(0, 8)}@primes4.com`;
    setClient((c) => ({
      ...c,
      company_name: profile.profile_name || c.company_name,
      contact_name: c.contact_name || "Demo Consultant",
      email: c.email || autoEmail,
      notes: profile.profile_description || c.notes,
    }));
    // Map seed-style enum values to Step 2 option labels so the user can
    // proceed without re-picking required fields. Anything unknown falls
    // back to the first valid option so the form validates immediately.
    const STRATEGIC_LABEL = {
      operational_excellence: "Cost Reduction",
      scalability_growth: "Throughput Improvement",
      cost_reduction: "Cost Reduction",
      inventory_accuracy: "Inventory Accuracy",
      automation_enablement: "Automation Enablement",
      digital_transformation: "Digital Transformation",
      service_level: "Service-Level Improvement",
      multi_site: "Multi-Site Standardization",
    };
    const BUSINESS_MODEL_LABEL = {
      b2b: "B2B Distribution",
      b2c: "B2C Fulfillment",
      both: "Hybrid",
      manufacturing: "Manufacturing",
      "3pl": "3PL",
      hybrid: "Hybrid",
    };
    const strategicLabel =
      STRATEGIC_LABEL[(profile.strategic_objective || "").toLowerCase()] ||
      profile.strategic_objective ||
      "";
    const businessModelLabel =
      BUSINESS_MODEL_LABEL[(profile.business_model || "").toLowerCase()] ||
      profile.business_model ||
      "";
    // Step 2 — engagement context.
    setCtx((s) => ({
      ...s,
      engagement_type: profile.engagement_type || s.engagement_type,
      industry: profile.industry || s.industry,
      strategic_objective: strategicLabel,
      business_model: businessModelLabel,
      // Default to "Global Network" — pharma / retail / consumer-goods
      // demos all ship multi-country sites.
      geographic_distribution: s.geographic_distribution || "Global Network",
      num_warehouse_sites: (profile.sites || []).length || s.num_warehouse_sites,
      priority: s.priority || "medium",
    }));
    // Step 2 — sites (local-only ids so Step 3 can bind findings to sites).
    const localSites = (profile.sites || []).map((s, idx) => ({
      ...s,
      id: `tmp-site-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      // Preserve site_code for backend mapping during final submit.
      site_code: s.site_code || "",
    }));
    setSites(localSites);
    // Step 3 — findings. Map to all sites by default (matches seed intent).
    const allSiteIds = localSites.map((s) => s.id);
    const localFindings = (profile.findings || []).map((f, idx) => ({
      ...f,
      id: `tmp-finding-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      // Normalise priority casing for the UI chips.
      priority: (f.priority || "Medium").toLowerCase(),
      applicable_site_ids: allSiteIds,
      // Store the original site_codes too so the backend can re-resolve
      // them when the user keeps "all sites" selected.
      _site_codes: localSites.map((s) => (s.site_code || "").toUpperCase()),
    }));
    setFindings(localFindings);
    setDemoMode(true);
    setDemoProfile(profile);
  };

  // Step 1 form

  // Public taxonomy + admin-curated suggestions surfaced inside the
  // Finding dialog. `processes` drives the dropdowns; `issueTemplates` /
  // `kpiTemplates` populate the suggestion sections when a process +
  // sub-process pair is picked.
  const [processes, setProcesses] = useState(BUSINESS_PROCESSES.map((p) => ({
    key: p.key, label: p.label,
    sub_processes: (p.sub || []).map((s) => ({ key: s, label: s })),
  })));
  const [issueTemplates, setIssueTemplates] = useState([]);
  const [kpiTemplates, setKpiTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // -------------------------------------------------------------------------
  // Resume on mount — strict entry-point semantics:
  //   • `?new=1`        → ALWAYS render a clean Step 1. Local cache is
  //                       ignored. Server-side drafts are left intact.
  //   • `?resume=<id>`  → Resume the explicit draft picked from the email-
  //                       gated "Resume Improvement" list on /improve.
  //   • no query params → Continue an in-session draft if one exists in
  //                       sessionStorage (handles mid-workshop refresh).
  //                       Otherwise render a clean Step 1.
  //
  // Critically, the legacy "auto-pick most-recent local draft" fallback is
  // gone — entry points are now explicit, so we never silently rehydrate a
  // stranger's draft on a shared browser.
  // -------------------------------------------------------------------------
  const SESSION_KEY = "smartimprove_active_draft_id";
  useEffect(() => {
    const explicitId = searchParams.get("resume");
    const forceNew = searchParams.get("new");
    if (forceNew) {
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      // Drop the marker so a refresh stays on the now-built draft (we'll
      // store its id in sessionStorage once Step 1 saves).
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    let targetId = explicitId;
    if (!targetId) {
      try { targetId = sessionStorage.getItem(SESSION_KEY); } catch { targetId = null; }
    }
    if (!targetId) return;
    const refs = listDraftRefs();
    const ref = refs.find((r) => r.id === targetId) || null;
    if (!ref) {
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      return;
    }
    (async () => {
      try {
        const data = await resumeIntakeDraft(ref.id, ref.resume_token);
        const eng = data.engagement || {};
        setDraft(ref);
        setClient({
          company_name: eng.company_name || "",
          contact_name: eng.contact_name || "",
          email: eng.email || "",
          phone: eng.phone || "",
          role: eng.role || "",
          assigned_consultant: eng.assigned_consultant || "",
          notes: eng.notes || "",
        });
        setCtx({
          engagement_type: eng.engagement_type || "workshop",
          industry: eng.industry || "",
          strategic_objective: eng.strategic_objective || "",
          geographic_distribution: eng.geographic_distribution || "",
          business_model: eng.business_model || "",
          num_warehouse_sites: eng.num_warehouse_sites || "",
          priority: eng.priority || "medium",
        });
        setSites(data.sites || []);
        setFindings(data.findings || []);
        // Jump to the deepest meaningful step.
        if ((data.findings || []).length) setStep(3);
        else setStep(2);
        // Keep the local ref fresh.
        saveDraftRef({
          ...ref,
          company_name: eng.company_name,
          email: eng.email,
          sites_count: (data.sites || []).length,
          findings_count: (data.findings || []).length,
        });
        // Persist the active draft id for the rest of this browser session
        // so a hard refresh keeps the user on their resumed draft.
        try { sessionStorage.setItem(SESSION_KEY, ref.id); } catch { /* ignore */ }
        // Drop the ?resume= query param so a hard refresh stays on the same
        // draft via sessionStorage rather than re-triggering the lookup.
        if (explicitId) {
          searchParams.delete("resume");
          setSearchParams(searchParams, { replace: true });
        }
        toast.info("Resumed your draft assessment");
      } catch {
        clearDraftRef(ref.id);
        try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the canonical process/sub-process taxonomy once on mount. Falls
  // back to the static BUSINESS_PROCESSES if the endpoint is unreachable
  // so the form remains usable offline.
  useEffect(() => {
    (async () => {
      try {
        const data = await getIntakeTaxonomy();
        if (Array.isArray(data?.processes) && data.processes.length) {
          setProcesses(data.processes);
        }
      } catch {
        /* keep static fallback */
      }
    })();
  }, []);

  // Fetch admin-curated Issue + KPI template suggestions whenever the
  // active finding form has a meaningful (process, sub-process) pair.
  // Templates are scoped tightly so we never show irrelevant items.
  useEffect(() => {
    if (!fDialog) return;
    const area = fForm?.operational_area;
    const subLabel = fForm?.sub_process;
    if (!area || !subLabel) {
      setIssueTemplates([]);
      setKpiTemplates([]);
      return;
    }
    const subKey = lookupSubKey(processes, area, subLabel);
    let cancelled = false;
    setTemplatesLoading(true);
    Promise.all([
      getIssueTemplateSuggestions(area, subKey || undefined).catch(() => []),
      getKpiTemplateSuggestions(area, subKey || undefined).catch(() => []),
    ]).then(([issues, kpis]) => {
      if (cancelled) return;
      setIssueTemplates(Array.isArray(issues) ? issues : []);
      setKpiTemplates(Array.isArray(kpis) ? kpis : []);
      setTemplatesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fDialog, fForm?.operational_area, fForm?.sub_process, processes]);

  // -------------------------------------------------------------------------
  // Step 1 — create draft
  // -------------------------------------------------------------------------
  const submitStep1 = async () => {
    if (submitting) return; // Belt-and-suspenders against rapid double-clicks.
    // Demo prefill mode — no backend write. Just advance to Step 2 so the
    // user can review the prefilled context + warehouse landscape. The
    // engagement is created atomically on final submit.
    if (demoMode) {
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      // If we already have a draft (resumed from sessionStorage or freshly
      // created earlier in this session and the user navigated back to Step 1
      // to edit), PATCH it instead of POSTing a brand-new engagement. The
      // previous behaviour created a duplicate row in the SmartImprove
      // consultant dashboard every time the user revisited Step 1.
      if (draft?.id && draft?.resume_token) {
        await patchEngagementDraft(draft.id, draft.resume_token, {
          company_name: client.company_name,
          email: client.email,
          contact_name: client.contact_name,
          phone: client.phone,
          role: client.role,
          assigned_consultant: client.assigned_consultant,
          notes: client.notes,
          engagement_type: ctx.engagement_type,
        });
        saveDraftRef({
          ...draft,
          company_name: client.company_name,
          email: client.email,
        });
        toast.success("Client details updated");
        setStep(2);
        return;
      }
      const resp = await startIntakeDraft({
        company_name: client.company_name,
        contact_name: client.contact_name,
        email: client.email,
        phone: client.phone,
        role: client.role,
        assigned_consultant: client.assigned_consultant,
        notes: client.notes,
        engagement_type: ctx.engagement_type,
      });
      const ref = {
        id: resp.id,
        resume_token: resp.resume_token,
        company_name: resp.company_name,
        email: resp.email,
        sites_count: 0,
        findings_count: 0,
      };
      setDraft(ref);
      saveDraftRef(ref);
      try { sessionStorage.setItem(SESSION_KEY, ref.id); } catch { /* ignore */ }
      toast.success("Client details saved — draft created");
      setStep(2);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 2 — save context, manage warehouses
  // -------------------------------------------------------------------------
  const saveCtx = async () => {
    // Demo prefill mode — keep context entirely in local state; no draft
    // engagement exists yet.
    if (demoMode) return;
    if (!draft) return;
    setSavingCtx(true);
    try {
      const patch = { ...ctx };
      if (patch.num_warehouse_sites === "" || patch.num_warehouse_sites == null) {
        delete patch.num_warehouse_sites;
      } else {
        patch.num_warehouse_sites = Number(patch.num_warehouse_sites);
      }
      await patchEngagementDraft(draft.id, draft.resume_token, patch);
      toast.success("Engagement context saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSavingCtx(false);
    }
  };

  const openAddWarehouse = () =>
    setWhForm({
      id: null,
      warehouse_name: "",
      country: "",
      warehouse_type: "",
      sap_landscape: "",
      ewm_version: "",
      site_size: "",
      automation_maturity: "",
      workforce_count: "",
      shifts_per_day: "",
      annual_inbound_deliveries: "",
      annual_outbound_orders: "",
      use_labor_cost_benchmark: false,
      labor_cost_per_hour_usd: "",
      inventory_value_usd: "",
      notes: "",
    }) || setWhDialog(true);
  const openEditWarehouse = (s) => {
    setWhForm({ ...s });
    setWhDialog(true);
  };
  const duplicateWarehouse = async (s) => {
    if (demoMode) {
      const newId = `tmp-site-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const dup = {
        ...s,
        id: newId,
        site_code: "",
        warehouse_name: `${s.warehouse_name} (copy)`,
      };
      setSites((prev) => [...prev, dup]);
      setWhForm(dup);
      setWhDialog(true);
      toast.success("Warehouse duplicated — adjust details and save");
      return;
    }
    if (!draft) return;
    const dup = {
      warehouse_name: `${s.warehouse_name} (copy)`,
      country: s.country || "",
      warehouse_type: s.warehouse_type || "",
      sap_landscape: s.sap_landscape || "",
      ewm_version: s.ewm_version || "",
      site_size: s.site_size || "",
      automation_maturity: s.automation_maturity || "",
      notes: s.notes || "",
    };
    try {
      const created = await addSiteDraft(draft.id, draft.resume_token, dup);
      setSites((prev) => [...prev, created]);
      setWhForm(created);
      setWhDialog(true);
      toast.success("Warehouse duplicated — adjust details and save");
    } catch {
      toast.error("Failed to duplicate warehouse");
    }
  };
  const deleteWarehouse = async (s) => {
    if (demoMode) {
      if (!window.confirm(`Delete warehouse "${s.warehouse_name}"?`)) return;
      setSites((prev) => prev.filter((x) => x.id !== s.id));
      setFindings((prev) =>
        prev.map((f) => ({
          ...f,
          applicable_site_ids: (f.applicable_site_ids || []).filter((id) => id !== s.id),
        }))
      );
      toast.success("Warehouse removed");
      return;
    }
    if (!draft) return;
    if (!window.confirm(`Delete warehouse "${s.warehouse_name}"?`)) return;
    try {
      await deleteSiteDraft(draft.id, s.id, draft.resume_token);
      setSites((prev) => prev.filter((x) => x.id !== s.id));
      // Strip the deleted site from any finding's applicable list.
      setFindings((prev) =>
        prev.map((f) => ({
          ...f,
          applicable_site_ids: (f.applicable_site_ids || []).filter((id) => id !== s.id),
        }))
      );
      toast.success("Warehouse removed");
    } catch {
      toast.error("Failed to delete warehouse");
    }
  };
  const saveWarehouse = async () => {
    if (demoMode) {
      if (!whForm) return;
      if (whForm.id) {
        setSites((prev) =>
          prev.map((x) => (x.id === whForm.id ? { ...whForm } : x))
        );
        toast.success("Warehouse updated");
      } else {
        const newId = `tmp-site-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const created = { ...whForm, id: newId };
        setSites((prev) => [...prev, created]);
        toast.success("Warehouse added");
      }
      setWhDialog(false);
      return;
    }
    if (!draft || !whForm) return;
    setSavingWh(true);
    try {
      if (whForm.id) {
        const updated = await updateSiteDraft(
          draft.id, whForm.id, draft.resume_token, whForm
        );
        setSites((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        toast.success("Warehouse updated");
      } else {
        const created = await addSiteDraft(draft.id, draft.resume_token, whForm);
        setSites((prev) => {
          const next = [...prev, created];
          if (draft) saveDraftRef({ ...draft, sites_count: next.length });
          return next;
        });
        toast.success("Warehouse added");
      }
      setWhDialog(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save warehouse");
    } finally {
      setSavingWh(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 3 — findings
  // -------------------------------------------------------------------------
  const openAddFinding = (procKey) => {
    setFForm({ ...EMPTY_FINDING, operational_area: procKey || "" });
    setFDialog(true);
  };
  const openEditFinding = (f) => {
    setFForm({
      ...EMPTY_FINDING,
      ...f,
      // Backward-compat: older drafts only carry `severity`. Surface it
      // as `priority` so the Gilead-style chip renders correctly.
      priority: f.priority || f.severity || "medium",
      impact_area: f.impact_area || "",
      applicable_site_ids: f.applicable_site_ids || [],
      kpi_current_value: f.kpi_current_value ?? null,
      kpi_target_value: f.kpi_target_value ?? null,
    });
    setFDialog(true);
  };
  const deleteFinding = async (f) => {
    if (demoMode) {
      if (!window.confirm(`Delete finding "${f.title}"?`)) return;
      setFindings((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Finding removed");
      return;
    }
    if (!draft) return;
    if (!window.confirm(`Delete finding "${f.title}"?`)) return;
    try {
      await deleteFindingDraft(draft.id, f.id, draft.resume_token);
      setFindings((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Finding removed");
    } catch {
      toast.error("Failed to delete finding");
    }
  };
  const saveFinding = async () => {
    if (demoMode) {
      if (!fForm) return;
      const localPayload = {
        ...fForm,
        priority: (fForm.priority || "medium").toLowerCase(),
        severity: (fForm.priority || fForm.severity || "medium").toLowerCase(),
      };
      if (fForm.id) {
        setFindings((prev) =>
          prev.map((x) => (x.id === fForm.id ? { ...x, ...localPayload } : x))
        );
        toast.success("Finding updated");
      } else {
        const newId = `tmp-finding-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const created = {
          ...localPayload,
          id: newId,
          applicable_site_ids: localPayload.applicable_site_ids || sites.map((s) => s.id),
        };
        setFindings((prev) => [...prev, created]);
        toast.success("Finding added");
      }
      setFDialog(false);
      return;
    }
    if (!draft || !fForm) return;
    setSavingF(true);
    try {
      const payload = {
        operational_area: fForm.operational_area,
        sub_process: fForm.sub_process,
        title: fForm.title,
        description: fForm.description,
        // Public UX → backend. Priority drives severity 1:1 so downstream
        // engines continue to read the same `severity` field.
        priority: fForm.priority || "medium",
        impact_area: fForm.impact_area || "",
        severity: fForm.priority || fForm.severity || "medium",
        frequency_volume: fForm.frequency_volume,
        observation_notes: fForm.observation_notes,
        root_cause: fForm.root_cause,
        existing_workaround: fForm.existing_workaround,
        applicable_site_ids: fForm.applicable_site_ids || [],
        kpi_metric_key: fForm.kpi_metric_key,
        kpi_metric_label: fForm.kpi_metric_label,
        kpi_current_value: fForm.kpi_current_value,
        kpi_target_value: fForm.kpi_target_value,
        kpi_unit: fForm.kpi_unit,
        kpi_direction: fForm.kpi_direction,
        issue_template_source: fForm.issue_template_source || "manual",
        issue_template_slug: fForm.issue_template_slug || "",
      };
      if (fForm.id) {
        const updated = await updateFindingDraft(
          draft.id, fForm.id, draft.resume_token, payload
        );
        setFindings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        toast.success("Finding updated");
      } else {
        const created = await addFindingDraft(draft.id, draft.resume_token, payload);
        setFindings((prev) => {
          const next = [...prev, created];
          if (draft) saveDraftRef({ ...draft, findings_count: next.length });
          return next;
        });
        toast.success("Finding added");
      }
      setFDialog(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save finding");
    } finally {
      setSavingF(false);
    }
  };

  // -------------------------------------------------------------------------
  // Final submit
  // -------------------------------------------------------------------------
  const submitAssessment = async () => {
    // Demo prefill mode — create everything atomically via the dedicated
    // backend endpoint, then route to the consultant workspace. No
    // public-intake draft was created during Steps 1/2/3 so there is no
    // resume_token to clear.
    if (demoMode) {
      setSubmitting(true);
      try {
        // Translate frontend findings back to the backend's shape. The
        // user may have edited applicable_site_ids — we pass them as
        // site_codes so the backend can resolve them to the freshly-
        // created site UUIDs. If the user kept "all sites" selected we
        // pass an empty list so the backend defaults to all.
        const sitesPayload = sites.map((s) => ({
          site_code: s.site_code || "",
          warehouse_name: s.warehouse_name || "",
          location_city: s.location_city || "",
          country: s.country || "",
          warehouse_type: s.warehouse_type || "",
          sap_landscape: s.sap_landscape || "",
          ewm_version: s.ewm_version || "",
          site_size: s.site_size || "",
          automation_maturity: s.automation_maturity || "",
          notes: s.notes || "",
        }));
        // Build a tempId → site_code map so we can translate per-finding
        // applicable_site_ids (which reference temp ids).
        const siteCodeByTempId = {};
        sites.forEach((s) => {
          if (s.id && s.site_code) siteCodeByTempId[s.id] = s.site_code;
        });
        const allSiteIds = sites.map((s) => s.id);
        const findingsPayload = findings.map((f) => {
          const selected = Array.isArray(f.applicable_site_ids)
            ? f.applicable_site_ids
            : [];
          // Treat "all sites selected" as empty → backend default.
          const isAll =
            selected.length === allSiteIds.length &&
            allSiteIds.every((id) => selected.includes(id));
          const codes = isAll
            ? []
            : selected
                .map((tempId) => siteCodeByTempId[tempId] || "")
                .filter(Boolean);
          return {
            operational_area: f.operational_area || "",
            sub_process: f.sub_process || "",
            title: f.title || "",
            description: f.description || "",
            priority: (f.priority || "medium"),
            impact_area: f.impact_area || "",
            applicable_site_ids: codes,
            kpi_metric_key: f.kpi_metric_key || "",
            kpi_metric_label: f.kpi_metric_label || "",
            kpi_current_value: f.kpi_current_value ?? null,
            kpi_target_value: f.kpi_target_value ?? null,
            kpi_unit: f.kpi_unit || "",
            kpi_direction: f.kpi_direction || "increase",
          };
        });
        const num =
          ctx.num_warehouse_sites === "" || ctx.num_warehouse_sites == null
            ? null
            : Number(ctx.num_warehouse_sites);
        const resp = await applySmartImproveDemoFromPayload({
          profile_id: demoProfile?.id || "",
          profile_name: demoProfile?.profile_name || "",
          client: {
            company_name: client.company_name || "",
            contact_name: client.contact_name || "",
            email: client.email || "",
            phone: client.phone || "",
            role: client.role || "",
            notes: client.notes || "",
          },
          context: {
            engagement_type: ctx.engagement_type || "workshop",
            industry: ctx.industry || "",
            business_model: ctx.business_model || "",
            strategic_objective: ctx.strategic_objective || "",
            geographic_distribution: ctx.geographic_distribution || "",
            priority: ctx.priority || "medium",
            num_warehouse_sites: num,
          },
          sites: sitesPayload,
          findings: findingsPayload,
        });
        toast.success(
          `Engagement created — ${resp.site_count} site${resp.site_count === 1 ? "" : "s"}, ${resp.finding_count} finding${resp.finding_count === 1 ? "" : "s"}`,
        );
        navigate(`/app/smartimprove/${resp.engagement_id}`);
        return;
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Failed to create engagement");
        return;
      } finally {
        setSubmitting(false);
      }
    }
    if (!draft) return;
    setSubmitting(true);
    try {
      const resp = await submitIntakeDraft(draft.id, draft.resume_token);
      setSubmittedAt(resp.submitted_at);
      // Only remove this specific draft — other unfinished drafts in
      // localStorage are preserved.
      clearDraftRef(draft.id);
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      setStep(4);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    if (draft?.id) clearDraftRef(draft.id);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    setDraft(null);
    setSubmittedAt(null);
    setDemoMode(false);
    setDemoProfile(null);
    setClient({
      company_name: "", contact_name: "", email: "",
      phone: "", role: "", assigned_consultant: "", notes: "",
    });
    setCtx({
      engagement_type: "workshop", industry: "", strategic_objective: "",
      geographic_distribution: "", business_model: "", num_warehouse_sites: "",
      priority: "medium",
    });
    setSites([]);
    setFindings([]);
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4" data-testid="improve-intake-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Workshop Intake
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Operational Transformation Assessment
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Capture your warehouse operations context, scope, and findings.
            Submit when you're done — our specialists handle the rest.
          </p>
        </div>

        {step <= TOTAL_STEPS ? <Stepper step={step} /> : null}

        {step === 1 ? (
          <Step1ClientDetails
            value={client}
            onChange={(p) => setClient((s) => ({ ...s, ...p }))}
            onSubmit={submitStep1}
            submitting={submitting}
            onDemoApplied={applyDemoProfileToState}
          />
        ) : step === 2 ? (
          <>
            <Step2BusinessWarehouse
              ctx={ctx}
              sites={sites}
              savingCtx={savingCtx}
              onCtxChange={(p) => setCtx((s) => ({ ...s, ...p }))}
              onCtxSave={saveCtx}
              onAddWarehouse={openAddWarehouse}
              onEditWarehouse={openEditWarehouse}
              onDuplicateWarehouse={duplicateWarehouse}
              onDeleteWarehouse={deleteWarehouse}
              onBack={() => setStep(1)}
              onContinue={async () => {
                await saveCtx();
                setStep(3);
              }}
            />
            <WarehouseDialog
              open={whDialog}
              onOpenChange={setWhDialog}
              value={whForm}
              onChange={setWhForm}
              onSave={saveWarehouse}
              saving={savingWh}
            />
          </>
        ) : step === 3 ? (
          <>
            <Step3OperationalAssessment
              findings={findings}
              sites={sites}
              onAddFinding={openAddFinding}
              onEditFinding={openEditFinding}
              onDeleteFinding={deleteFinding}
              onBack={() => setStep(2)}
              onSubmit={submitAssessment}
              submitting={submitting}
            />
            <FindingDialog
              open={fDialog}
              onOpenChange={setFDialog}
              value={fForm}
              onChange={setFForm}
              onSave={saveFinding}
              saving={savingF}
              sites={sites}
              processes={processes}
              issueTemplates={issueTemplates}
              kpiTemplates={kpiTemplates}
              templatesLoading={templatesLoading}
            />
          </>
        ) : (
          <Step4Submitted
            submittedAt={submittedAt}
            onRestart={restart}
            onBackToSmartImprove={() => navigate("/improve")}
          />
        )}

        {step < 4 && sites.length === 0 && step === 3 ? (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-sm px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            You haven't added any warehouses yet — add at least one site in
            Step 2 before assigning findings.
          </div>
        ) : null}
      </div>
    </div>
  );
}
